<template>
  <v-app>
    <!-- Unified Navigation Drawer -->
    <v-navigation-drawer v-model="drawer" app temporary :color="isLoggedIn ? 'primary' : undefined">
      <!-- Logged-in user header -->
      <v-container v-if="isLoggedIn">
        <v-row justify="center">
          <v-col class="mt-5 text-center">
            <v-avatar size="100" color="grey-lighten-2">
              <v-img v-if="user.image_url" :src="user.image_url" :alt="user.name" cover>
                <template v-slot:placeholder>
                  <div class="d-flex fill-height align-center justify-center">
                    <v-progress-circular indeterminate color="grey-lighten-5" size="24"></v-progress-circular>
                  </div>
                </template>
                <template v-slot:error>
                  <div class="d-flex fill-height align-center justify-center">
                    <v-icon size="48" color="grey">mdi-account-circle</v-icon>
                  </div>
                </template>
              </v-img>
              <v-icon v-else size="48" color="grey">mdi-account-circle</v-icon>
            </v-avatar>
            <p class="text-white subheading my-1">{{ user.name }}</p>
          </v-col>
        </v-row>
        <v-row v-if="!userStore.hasFullAccess" justify="center" class="mb-2">
          <v-col cols="10">
            <v-btn to="/pricing" color="warning" variant="flat" block rounded="pill" class="text-none font-weight-bold">
              <v-icon start>mdi-lock-open-variant</v-icon>
              Unlock Full Access
            </v-btn>
          </v-col>
        </v-row>
        <v-row v-if="userStore.hasFullAccess" justify="center" class="my-0">
          <v-col cols="auto" class="pa-0">
            <v-chip :color="userStore.tierColor" variant="flat" size="small">
              <v-icon start size="14">{{ userStore.tierIcon }}</v-icon>
              {{ userStore.tierShortName }}
            </v-chip>
          </v-col>
        </v-row>
        <v-row justify="center" class="my-0">
          <v-col cols="auto" class="pa-0">
            <v-menu location="bottom center">
              <template #activator="{ props }">
                <v-chip v-bind="props" :color="isOffline ? 'warning' : 'success'" variant="flat" size="small"
                  aria-live="polite" class="cursor-pointer font-weight-bold">
                  <v-icon start size="14">{{ isOffline ? 'mdi-cloud-off-outline' : 'mdi-cloud-check-outline' }}</v-icon>
                  {{ isOffline ? 'Offline' : 'Online' }}
                </v-chip>
              </template>
              <v-card max-width="300">
                <v-card-title class="text-body-1">Connection status</v-card-title>
                <v-card-text>
                  This shows whether Shelf can reach the internet. Offline mode keeps downloaded data available, but
                  network-only actions such as AI, scanning, and search require a connection.
                </v-card-text>
              </v-card>
            </v-menu>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <CurrentHouseholdAndPantrySelector stacked appearance="transparent" variant="outlined"
              density="comfortable" />
          </v-col>
        </v-row>
      </v-container>

      <!-- Guest header -->
      <v-container v-else class="py-6">
        <div class="d-flex align-center justify-center">
          <ShelfLogo height="120" alt="Shelf" class="app-logo app-logo-drawer" />
        </div>
      </v-container>

      <!-- Navigation links -->
      <v-list nav>
        <template v-if="isLoggedIn">
          <v-list-item v-for="link in authLinks" :key="link.route" :to="link.route" @click="drawer = false">
            <template v-slot:prepend>
              <v-icon class="text-white">{{ link.icon }}</v-icon>
            </template>
            <v-list-item-title class="text-white">{{ link.text }}</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="userStore.membershipTier === 'creator'" to="/admin" @click="drawer = false">
            <template v-slot:prepend>
              <v-icon class="text-white">mdi-gift</v-icon>
            </template>
            <v-list-item-title class="text-white">Creator Gifts</v-list-item-title>
          </v-list-item>
        </template>
        <template v-else>
          <v-list-item v-for="link in guestLinks" :key="link.route" :to="link.route" @click="drawer = false"
            :prepend-icon="link.icon" :title="link.text" />
        </template>
      </v-list>

      <!-- Footer actions -->
      <v-container>
        <v-row justify="center">
          <v-col cols="12" class="text-center">
            <v-btn v-if="isLoggedIn" @click="logout()" color="error" block>
              <span>Logout</span>
              <v-icon end>mdi-logout</v-icon>
            </v-btn>
            <v-btn v-else to="/login" color="primary" block @click="drawer = false">
              <span>Sign In / Sign Up</span>
              <v-icon end>mdi-login</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-navigation-drawer>

    <v-snackbar v-model="snackbar" :timeout="snackbarDuration" location="top" variant="tonal" color="surface"
      density="compact" rounded="lg" max-width="min(90vw, 420px)">
      {{ snackbarMessage }}
      <v-btn @click="snackbar = false" icon size="small" aria-label="Close notification">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-snackbar>

    <!-- Unified App Bar -->
    <v-app-bar :color="isLoggedIn ? 'primary' : undefined" :flat="!isLoggedIn" ref="header"
      :scroll-behavior="isLoggedIn ? 'hide' : undefined">
      <v-app-bar-nav-icon aria-label="Open navigation menu" @click="drawer = !drawer"
        :color="isLoggedIn ? 'white' : undefined" />

      <router-link v-if="!hasLargeMainLogo" to="/" class="d-flex align-center text-decoration-none">
        <h1 class="app-bar-title">SHELF</h1>
      </router-link>

      <v-spacer />

      <!-- Search (logged-in only) -->
      <template v-if="isLoggedIn">
        <v-text-field solo light clearable :loading="loadingSearchResults" :hide-details="true" ref="searchBar"
          style="max-width: 0%; opacity: 0" @input="onSearchBarInput" @change="onSearchBarChange"
          @blur="onSearchBarBlur" v-if="isSearchEnabled" prepend-inner-icon="mdi-magnify" :label="searchLabel">
        </v-text-field>
        <v-btn icon v-if="isSearchEnabled && !isSearchActive" aria-label="Open search" @click="showSearchBar">
          <v-icon>mdi-magnify</v-icon>
        </v-btn>

        <!-- AI Notifications -->
        <AiNotifications />
      </template>

      <!-- Guest: desktop nav links -->
      <template v-if="!isLoggedIn">
        <v-btn icon variant="text" :aria-label="guestDarkMode ? 'Use light mode' : 'Use dark mode'"
          :title="guestDarkMode ? 'Use light mode' : 'Use dark mode'" @click="toggleGuestTheme">
          <v-icon>{{ guestDarkMode ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
        </v-btn>
        <v-btn variant="text" class="text-none d-none d-sm-flex" to="/discover">
          Recipes
        </v-btn>
        <v-btn variant="text" class="text-none d-none d-sm-flex" to="/features">
          Features
        </v-btn>
        <v-btn variant="text" class="text-none d-none d-sm-flex" to="/pricing">
          Pricing
        </v-btn>
        <v-btn variant="outlined" color="primary" class="text-none ml-2" to="/login">
          Sign In
        </v-btn>
      </template>
    </v-app-bar>

    <!-- Sizes your content based upon application components -->
    <v-main class="bg-background">
      <v-alert v-if="isOffline" type="warning" variant="tonal" density="compact" class="offline-banner ma-2"
        role="status" closable>
        <strong>You're offline.</strong> Previously downloaded Firestore data remains available. Simple Firestore
        edits may sync when you reconnect, but multi-step saves are not guaranteed to complete offline.
        AI, image, scanning, search, and other network-only actions need an internet connection.
      </v-alert>
      <router-view></router-view>
      <div style="height: 100px">
        <!-- Gives extra space so that once scrolled to the bottom, content can go above any potential floating action button -->
      </div>
      <FeedbackDialog />
      <LoginPromptDialog />
    </v-main>

    <SiteFooter @logout="logout" />

    <v-footer app v-if="inDevEnv()" class="px-3">
      <span style="font-size: 12px">Dev info: size({{ display.name.value }})</span>
    </v-footer>
  </v-app>
</template>

<script setup lang="ts">
import { auth } from "@/fb"
import { debug } from "@/logger"
import CurrentHouseholdAndPantrySelector from "@/components/CurrentHouseholdAndPantrySelector.vue"
import FeedbackDialog from "@/components/FeedbackDialog.vue"
import LoginPromptDialog from "@/components/LoginPromptDialog.vue"
import AiNotifications from "@/components/AiNotifications.vue"
import ShelfLogo from "@/components/ShelfLogo.vue"
import SiteFooter from "@/components/SiteFooter.vue"
import { useAppState } from "@/composables/useAppState"
import { usePendingAction } from "@/composables/usePendingAction"
import { useUserStore } from "@/stores/userStore"
import { useRecipeStore } from "@/stores/recipeStore"
import { useAiJobStore } from "@/stores/aiJobStore"
import { ref, computed, watch, onMounted, nextTick } from "vue"
import { useTheme, useDisplay } from 'vuetify'
import { useRoute, useRouter } from "vue-router"
import { loadGoogleFonts } from "@/utils/fontLoader"
import { useNetworkStatus } from "@/composables/useNetworkStatus"

const appState = useAppState()
const { consumePendingAction, hasPendingAction } = usePendingAction()
const userStore = useUserStore()
const recipeStore = useRecipeStore()
const aiJobStore = useAiJobStore()
const { isOffline } = useNetworkStatus()
const theme = useTheme()
const display = useDisplay()
const route = useRoute()
const router = useRouter()

// Template refs
const header = ref<any>(null)
const searchBar = ref<any>(null)

// Component state
const drawer = ref(false)
const isScrolled = ref(false)
const isSearchActive = ref(false)
const submitSearchTimeout = ref<number | null>(null)
const loadingSearchResults = ref(false)
const appBarAnimation = ref<Animation | null>(null)
const searchBarAnimation = ref<Animation | null>(null)

// Navigation links for authenticated users
const authLinks = [
  { text: "Home", icon: "mdi-home", route: "/home" },
  { text: "Pantry", icon: "mdi-fridge", route: "/pantry" },
  {
    text: "Recipe Books",
    icon: "mdi-bookshelf",
    route: "/recipe_books"
  },
  { text: "Meal Planning", icon: "mdi-calendar-month", route: "/meal_planning" },
  {
    text: "Shopping Lists",
    icon: "mdi-cart",
    route: "/shopping_lists"
  },
  { text: "Discover Recipes", icon: "mdi-chef-hat", route: "/discover" },
  { text: "Settings", icon: "mdi-cog", route: "/settings" }
]

// Navigation links for guests
const guestLinks = [
  { text: "Home", icon: "mdi-home", route: "/" },
  { text: "Browse Recipes", icon: "mdi-chef-hat", route: "/discover" },
  { text: "Features", icon: "mdi-feature-search", route: "/features" },
  { text: "Pricing", icon: "mdi-star", route: "/pricing" }
]

// Computed properties
const title = computed(() => appState.title.value)
const isSearchEnabled = computed(() => appState.searchEnabled.value)
const searchLabel = computed(() => appState.searchPlaceholder.value)

const snackbar = computed({
  get: () => appState.snackbar.value.show,
  set: (value: boolean) => {
    if (!value) {
      appState.hideSnackbar()
    }
  }
})

const snackbarMessage = computed(() => appState.snackbar.value.message)
const snackbarDuration = computed(() => appState.snackbar.value.duration)
const user = computed(() => userStore.user)
const isLoggedIn = computed(() => userStore.isLoggedIn)
const isPremium = computed(() => userStore.isPremium)
const colors = computed(() => userStore.colors)
const hasLargeMainLogo = computed(() => route.name === 'landing' || route.name === 'login')
const guestDarkMode = ref(getInitialGuestDarkMode())
const effectiveDarkMode = computed(() => isLoggedIn.value ? userStore.darkMode : guestDarkMode.value)

function getInitialGuestDarkMode(): boolean {
  if (typeof window === 'undefined') return false

  const savedPreference = window.localStorage.getItem('shelf-guest-dark-mode')
  if (savedPreference !== null) return savedPreference === 'true'

  return false
}

// Methods
const inDevEnv = () => {
  return process.env.NODE_ENV === "development" && userStore.user && userStore.user.isDev
}

const logout = () => {
  // Note: unbindAll is handled automatically by data.ts onUserChanged when auth state changes
  auth.signOut()
}

const toggleGuestTheme = () => {
  guestDarkMode.value = !guestDarkMode.value
  window.localStorage.setItem('shelf-guest-dark-mode', String(guestDarkMode.value))
}

const onSearchBarInput = (params: string) => {
  if (submitSearchTimeout.value) {
    clearTimeout(submitSearchTimeout.value)
  }
  submitSearchTimeout.value = window.setTimeout(() => {
    recipeStore.setSearchQuery(params)
  }, 500)
}

const onSearchBarChange = () => {
  // Called when text is submitted by pushing "Enter"
  if (searchBar.value) {
    searchBar.value.blur()
    if (isSearchActive.value && !searchBar.value.internalValue) {
      hideSearchBar()
    }
  }
}

const onSearchBarBlur = () => {
  if (searchBar.value && isSearchActive.value && !searchBar.value.internalValue) {
    hideSearchBar()
  }
}

const showSearchBar = () => {
  isSearchActive.value = true

  if (header.value) {
    const appBarEffect = new KeyframeEffect(
      header.value.$el,
      [{ minWidth: "100px" }, { minWidth: "300px" }],
      {
        duration: 500,
        fill: "both"
      }
    )
    appBarAnimation.value = new Animation(appBarEffect)
  }

  if (searchBar.value) {
    const searchBarEffect = new KeyframeEffect(
      searchBar.value.$el,
      [
        { maxWidth: "0px", opacity: "0" },
        { maxWidth: "100%", opacity: "1" }
      ],
      {
        duration: 500,
        fill: "both"
      }
    )
    searchBarAnimation.value = new Animation(searchBarEffect)
  }

  appBarAnimation.value?.play()
  searchBarAnimation.value?.play()

  nextTick(() => {
    searchBar.value?.focus()
  })
}

const hideSearchBar = () => {
  debug("Hiding Search Bar")
  appBarAnimation.value?.reverse()
  searchBarAnimation.value?.reverse()
  isSearchActive.value = false
}

// Helper to apply fonts
const applyFonts = (headerFont: string | undefined, bodyFont: string | undefined, fontSize: number | undefined, loadFonts = false) => {
  const hf = headerFont ?? 'Acme, sans-serif'
  const bf = bodyFont ?? 'Karla, sans-serif'
  const fs = fontSize ?? 110
  if (loadFonts) loadGoogleFonts([hf, bf])
  document.documentElement.style.setProperty('--font-header', hf)
  document.documentElement.style.setProperty('--font-body', bf)
  document.documentElement.style.fontSize = `${fs}%`
  document.body.style.fontFamily = bf
}

// Watchers
watch(() => userStore.colors, (newValue) => {
  debug("Colors changed (composition API)!")
  debug(newValue)
  console.log("Colors watcher triggered with:", newValue)

  if (newValue) {
    console.log('✅ Colors available, applying to theme')
    try {
      // Update both light and dark theme colors
      const currentThemeName = theme.global.name.value
      const currentTheme = theme.themes.value[currentThemeName]

      if (currentTheme && currentTheme.colors) {
        console.log('Before update - theme colors:', { ...currentTheme.colors })

        // Apply colors to current theme
        Object.keys(newValue).forEach(key => {
          // Skip background - handled separately based on mode
          if (key === 'background' || key === 'darkBackground') return

          if (newValue[key]) {
            currentTheme.colors[key] = newValue[key]!
          }
        })

        // Handle background based on current mode
        if (currentThemeName === 'dark') {
          if (newValue.darkBackground) {
            currentTheme.colors.background = newValue.darkBackground
          }
        } else {
          // Light mode
          if (newValue.background) {
            currentTheme.colors.background = newValue.background
          }
        }

        console.log('After update - theme colors:', { ...currentTheme.colors })
      }
    } catch (error) {
      console.warn('Could not update theme colors:', error)
    }
  } else {
    console.log('❌ No colors to apply')
  }
}, { deep: true })

// Watch for dark mode changes
watch(effectiveDarkMode, (isDark) => {
  console.log('Dark mode changed:', isDark)
  theme.global.name.value = isDark ? 'dark' : 'light'

  // Update color-scheme on root element to prevent OS dark mode overrides
  document.documentElement.classList.toggle('dark-theme', isDark ?? false)

  // Re-apply user colors to the new theme
  const colors = userStore.colors
  if (colors) {
    const currentTheme = theme.themes.value[isDark ? 'dark' : 'light']
    if (currentTheme?.colors) {
      Object.keys(colors).forEach(key => {
        // Skip background - handled separately based on mode
        if (key === 'background' || key === 'darkBackground') return

        if (colors[key]) {
          currentTheme.colors[key] = colors[key]!
        }
      })

      // Handle background based on mode
      if (isDark) {
        if (colors.darkBackground) {
          currentTheme.colors.background = colors.darkBackground
        }
      } else {
        if (colors.background) {
          currentTheme.colors.background = colors.background
        }
      }
    }
  }
}, { immediate: true })

// Watch for font changes
watch([() => userStore.fonts, () => userStore.isLoggedIn], ([newFonts, loggedIn]) => {
  console.log('Fonts changed:', newFonts)
  applyFonts(newFonts?.headerFont, newFonts?.bodyFont, newFonts?.fontSize, loggedIn)
}, { deep: true, immediate: true })

watch(() => route.path, () => {
  appState.disableSearch()
})

// Watch for login to execute pending actions
watch(() => isLoggedIn.value, async (loggedIn, wasLoggedIn) => {
  if (loggedIn && !wasLoggedIn && hasPendingAction()) {
    // User just logged in and there's a pending action
    const action = consumePendingAction()
    if (action) {
      debug('Executing pending action after login:', action)

      // Give the stores a moment to initialize
      await nextTick()
      await new Promise(resolve => setTimeout(resolve, 500))

      switch (action.type) {
        case 'addToRecipeBook':
          // Navigate to the recipe page where they can add it to a book
          if (action.payload?.recipeId) {
            router.push(`/recipe/${action.payload.recipeId}`)
          }
          break
        case 'cloneRecipe':
          if (action.payload?.recipeId) {
            router.push(`/recipe/${action.payload.recipeId}`)
          }
          break
        // Add more action handlers as needed
        default:
          debug('Unknown pending action type:', action.type)
      }
    }
  }
})

// Watch for AI job completions
watch(() => aiJobStore.completedCount, (newCount, oldCount) => {
  // Ignore initial load (when loading is true)
  if (aiJobStore.loading) return

  // Only notify on increase
  if (newCount > oldCount) {
    appState.showSnackbar('AI Job Completed!', 5000)
  }
})

// Lifecycle
onMounted(() => {
  console.log('App mounted')
  console.log('User colors:', colors.value)

  debug("App Mounted()")

  if (isLoggedIn.value && header.value) {
    // Watch for scroll changes on the header ref
    watch(
      () => header.value?.currentScroll,
      (val: number) => {
        isScrolled.value = val > 0
      }
    )
  }
})

console.log('App created')
console.log('User colors:', colors.value)
</script>

<style>
/* Use Vuetify's color classes for disabled items:
   Add 'bg-orange-lighten-4 text--secondary' classes to disabled v-list-item components */
.app-logo {
  box-sizing: content-box;
}

.app-logo-drawer {
  padding: 12px;
}

.app-logo-bar {
  padding: 8px;
  box-sizing: border-box;
}

.app-bar-title {
  color: rgb(var(--v-theme-primary));
  font-size: 2.5rem !important;
  line-height: 1;
  margin: 0 0 0 16px;
}

.offline-banner {
  position: relative;
}
</style>
